import { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, CircularProgress, Alert, Chip, IconButton, Tooltip,
} from '@mui/material';
import { Add as AddIcon, Visibility as ViewIcon, Delete as DeleteIcon, CheckCircle as ReceiveIcon } from '@mui/icons-material';
import { api } from '../services/apiClient';
import { API_ENDPOINTS } from '../config/api';

interface POItem {
  product_name: string;
  quantity_ordered: number;
  unit_cost: number;
  product_id?: number;
}

interface PurchaseOrder {
  id: number;
  po_number: string;
  supplier_name: string;
  supplier_email?: string;
  expected_date?: string;
  total_amount: number;
  status: string;
  created_at: string;
  created_by_name?: string;
  notes?: string;
  items?: (POItem & { quantity_received?: number; id?: number })[];
}

const statusColor = (s: string): 'default' | 'primary' | 'success' | 'warning' | 'error' => {
  switch (s) {
    case 'Pending': return 'warning';
    case 'Ordered': return 'primary';
    case 'Received': return 'success';
    case 'Cancelled': return 'error';
    default: return 'default';
  }
};

const emptyItem: POItem = { product_name: '', quantity_ordered: 1, unit_cost: 0 };

const PurchaseOrdersPage = () => {
  const [orders, setOrders] = useState<PurchaseOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ supplier_name: '', supplier_email: '', expected_date: '', notes: '' });
  const [poItems, setPoItems] = useState<POItem[]>([{ ...emptyItem }]);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [viewOrder, setViewOrder] = useState<PurchaseOrder | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PurchaseOrder | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchOrders = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<{ data: PurchaseOrder[] }>(API_ENDPOINTS.PURCHASE_ORDERS.LIST);
      setOrders(res.data || []);
    } catch {
      setError('Failed to load purchase orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOrders(); }, []);

  const openCreate = () => {
    setForm({ supplier_name: '', supplier_email: '', expected_date: '', notes: '' });
    setPoItems([{ ...emptyItem }]);
    setFormError(null);
    setCreateOpen(true);
  };

  const updateItem = (index: number, field: keyof POItem, value: string | number) => {
    setPoItems(prev => prev.map((it, i) => i === index ? { ...it, [field]: value } : it));
  };

  const addItem = () => setPoItems(prev => [...prev, { ...emptyItem }]);
  const removeItem = (i: number) => setPoItems(prev => prev.filter((_, idx) => idx !== i));

  const handleCreate = async () => {
    if (!form.supplier_name.trim()) { setFormError('Supplier name is required'); return; }
    if (poItems.some(it => !it.product_name.trim())) { setFormError('All items need a product name'); return; }
    setSaving(true);
    setFormError(null);
    try {
      await api.post(API_ENDPOINTS.PURCHASE_ORDERS.CREATE, {
        ...form,
        items: poItems.map(it => ({ ...it, quantity_ordered: Number(it.quantity_ordered), unit_cost: Number(it.unit_cost) })),
      });
      setCreateOpen(false);
      fetchOrders();
    } catch {
      setFormError('Failed to create purchase order');
    } finally {
      setSaving(false);
    }
  };

  const handleView = async (po: PurchaseOrder) => {
    try {
      const res = await api.get<PurchaseOrder>(API_ENDPOINTS.PURCHASE_ORDERS.DETAIL(po.id));
      setViewOrder(res);
    } catch {
      setViewOrder(po);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`${API_ENDPOINTS.PURCHASE_ORDERS.DETAIL(deleteTarget.id)}`);
      setDeleteTarget(null);
      fetchOrders();
    } catch {
      // ignore
    } finally {
      setDeleting(false);
    }
  };

  const handleReceive = async (po: PurchaseOrder) => {
    if (!viewOrder?.items?.length) return;
    try {
      await api.post(API_ENDPOINTS.PURCHASE_ORDERS.RECEIVE(po.id), {
        items: viewOrder.items.map(it => ({
          purchase_order_item_id: it.id,
          quantity_received: it.quantity_ordered,
        })),
      });
      setViewOrder(null);
      fetchOrders();
    } catch {
      // ignore
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Purchase Orders</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>New Order</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>PO #</TableCell>
                <TableCell>Supplier</TableCell>
                <TableCell>Expected</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created By</TableCell>
                <TableCell align="center" width={120}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.length === 0 ? (
                <TableRow><TableCell colSpan={7} align="center">No purchase orders</TableCell></TableRow>
              ) : orders.map(po => (
                <TableRow key={po.id} hover>
                  <TableCell>{po.po_number}</TableCell>
                  <TableCell>{po.supplier_name}</TableCell>
                  <TableCell>{po.expected_date ? new Date(po.expected_date).toLocaleDateString() : '—'}</TableCell>
                  <TableCell align="right">${(po.total_amount || 0).toFixed(2)}</TableCell>
                  <TableCell><Chip label={po.status} color={statusColor(po.status)} size="small" /></TableCell>
                  <TableCell>{po.created_by_name || '—'}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="View"><IconButton size="small" onClick={() => handleView(po)}><ViewIcon fontSize="small" /></IconButton></Tooltip>
                    {po.status !== 'Received' && (
                      <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeleteTarget(po)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>New Purchase Order</DialogTitle>
        <DialogContent>
          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <Box display="flex" gap={2}>
              <TextField label="Supplier Name *" value={form.supplier_name} onChange={e => setForm(f => ({ ...f, supplier_name: e.target.value }))} fullWidth />
              <TextField label="Supplier Email" value={form.supplier_email} onChange={e => setForm(f => ({ ...f, supplier_email: e.target.value }))} fullWidth />
            </Box>
            <Box display="flex" gap={2}>
              <TextField label="Expected Date" type="date" InputLabelProps={{ shrink: true }} value={form.expected_date} onChange={e => setForm(f => ({ ...f, expected_date: e.target.value }))} fullWidth />
              <TextField label="Notes" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} fullWidth />
            </Box>
            <Typography variant="subtitle2" mt={1}>Items</Typography>
            {poItems.map((it, i) => (
              <Box key={i} display="flex" gap={1} alignItems="center">
                <TextField label="Product" value={it.product_name} onChange={e => updateItem(i, 'product_name', e.target.value)} sx={{ flex: 2 }} size="small" />
                <TextField label="Qty" type="number" value={it.quantity_ordered} onChange={e => updateItem(i, 'quantity_ordered', e.target.value)} sx={{ width: 80 }} size="small" inputProps={{ min: 1 }} />
                <TextField label="Unit Cost" type="number" value={it.unit_cost} onChange={e => updateItem(i, 'unit_cost', e.target.value)} sx={{ width: 110 }} size="small" inputProps={{ min: 0, step: 0.01 }} />
                <IconButton size="small" color="error" onClick={() => removeItem(i)} disabled={poItems.length === 1}><DeleteIcon fontSize="small" /></IconButton>
              </Box>
            ))}
            <Button variant="outlined" size="small" onClick={addItem} sx={{ alignSelf: 'flex-start' }}>+ Add Item</Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={saving}>{saving ? 'Creating...' : 'Create'}</Button>
        </DialogActions>
      </Dialog>

      {/* View / Receive dialog */}
      <Dialog open={!!viewOrder} onClose={() => setViewOrder(null)} maxWidth="sm" fullWidth>
        <DialogTitle>PO {viewOrder?.po_number} — {viewOrder?.supplier_name}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" mb={2}>Status: {viewOrder?.status}</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Product</TableCell>
                <TableCell align="right">Ordered</TableCell>
                <TableCell align="right">Unit Cost</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(viewOrder?.items || []).map((it, i) => (
                <TableRow key={i}>
                  <TableCell>{it.product_name}</TableCell>
                  <TableCell align="right">{it.quantity_ordered}</TableCell>
                  <TableCell align="right">${(it.unit_cost || 0).toFixed(2)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewOrder(null)}>Close</Button>
          {viewOrder?.status !== 'Received' && (
            <Button variant="contained" color="success" startIcon={<ReceiveIcon />} onClick={() => viewOrder && handleReceive(viewOrder)}>
              Mark Received
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete Purchase Order</DialogTitle>
        <DialogContent>
          <Typography>Delete <strong>{deleteTarget?.po_number}</strong> from {deleteTarget?.supplier_name}?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}>{deleting ? 'Deleting...' : 'Delete'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PurchaseOrdersPage;
